import { useEffect, useMemo, useRef, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Wallet, Pencil, RefreshCw, ChevronDown, ChevronRight, Lock, Unlock,
  Download, Upload, History, AlertTriangle, GitCompare,
} from "lucide-react";
import { toast } from "sonner";

const fmt = (n) => (typeof n === "number" ? n.toFixed(2) : "0.00");
const monthLabel = (iso) => {
  if (!iso) return "—";
  const [y, m] = iso.split("-");
  return new Date(parseInt(y), parseInt(m) - 1, 1)
    .toLocaleDateString("en-IN", { month: "short", year: "numeric" });
};
const thisMonthValue = () => new Date().toISOString().slice(0, 7);
const REASON_CODES = [
  "Leave approved on WhatsApp", "Biometric missed punch", "Management decision",
  "Correction — data entry error", "Migrated from old ledger", "Other",
];

export default function LeaveLedger() {
  const { user } = useAuth();
  const isSuperAdmin = user?.role === "super_admin";

  const [units, setUnits] = useState([]);
  const [unitId, setUnitId] = useState("");
  const [filterMonth, setFilterMonth] = useState(thisMonthValue());
  const [search, setSearch] = useState("");
  const [adjustedOnly, setAdjustedOnly] = useState(false);
  const [negativeOnly, setNegativeOnly] = useState(false);

  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lockInfo, setLockInfo] = useState({ locked: false });
  const [expanded, setExpanded] = useState(null); // employee_id currently expanded
  const [detail, setDetail] = useState(null);

  const [editing, setEditing] = useState(null); // opening/accrued override dialog
  const [adjusting, setAdjusting] = useState(null); // {employee_id, name, code, month, field, current, new_value, reason_code, reason_text}
  const [unlocking, setUnlocking] = useState(false);
  const [unlockReason, setUnlockReason] = useState("");
  const [auditOpen, setAuditOpen] = useState(false);
  const [auditRows, setAuditRows] = useState([]);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkFile, setBulkFile] = useState(null);
  const [bulkPreview, setBulkPreview] = useState(null);
  const [beforeAfterOpen, setBeforeAfterOpen] = useState(false);
  const [beforeAfter, setBeforeAfter] = useState(null);
  const [beforeAfterLoading, setBeforeAfterLoading] = useState(false);

  const monthIso = `${filterMonth}-01`;

  useEffect(() => {
    api.get("/units").then(({ data }) => setUnits(data || [])).catch(() => {});
  }, []);

  const loadSeq = useRef(0);
  const load = async () => {
    if (!unitId) { setRows([]); setSummary(null); setLoading(false); return; }
    const seq = ++loadSeq.current;
    setLoading(true);
    try {
      const params = { unit_id: unitId, month: monthIso, adjusted_only: adjustedOnly, negative_only: negativeOnly };
      if (search) params.search = search;
      const [{ data }, { data: s }, { data: lk }] = await Promise.all([
        api.get("/leave-ledger-v2", { params }),
        api.get("/leave-ledger-v2/summary", { params: { unit_id: unitId, month: monthIso } }),
        api.get("/leave-ledger-v2/lock-status", { params: { unit_id: unitId, month: monthIso } }),
      ]);
      if (seq !== loadSeq.current) return; // a newer request already started — discard this stale response
      setRows(data?.rows || []);
      setSummary(s);
      setLockInfo(lk);
    } catch (e) {
      if (seq === loadSeq.current) toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      if (seq === loadSeq.current) setLoading(false);
    }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [unitId, filterMonth, adjustedOnly, negativeOnly]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { const t = setTimeout(load, 350); return () => clearTimeout(t); }, [search]);

  const toggleExpand = async (r) => {
    if (expanded === r.employee_id) { setExpanded(null); setDetail(null); return; }
    setExpanded(r.employee_id);
    setDetail(null);
    try {
      const { data } = await api.get(`/leave-ledger-v2/${r.employee_id}/${monthIso}/detail`);
      setDetail(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  // ---------------- Opening/Accrued override (legacy, unchanged) ----------------
  const openEdit = (r) => setEditing({
    employee_id: r.employee_id, name: r.employee_name, code: r.employee_code,
    month: r.month,
    opening_el: r.opening_override_el ?? r.opening_el ?? 0, opening_pl: r.opening_override_pl ?? r.opening_pl ?? 0,
    manual_accrued_el: r.manual_accrued_el ?? 0, manual_accrued_pl: r.manual_accrued_pl ?? 0,
    reason: "",
  });
  const saveOpening = async () => {
    if (!editing) return;
    try {
      await api.post("/attendance/leave-ledger/opening", {
        employee_id: editing.employee_id, month: editing.month,
        opening_el: parseFloat(editing.opening_el) || 0,
        opening_pl: parseFloat(editing.opening_pl) || 0,
        manual_accrued_el: parseFloat(editing.manual_accrued_el) || 0,
        manual_accrued_pl: parseFloat(editing.manual_accrued_pl) || 0,
        reason: editing.reason || null,
      });
      toast.success("Opening balance saved");
      setEditing(null);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  // ---------------- New: Adjustment (Step 3) ----------------
  const openAdjust = (r, field) => setAdjusting({
    employee_id: r.employee_id, name: r.employee_name, code: r.employee_code, month: r.month,
    calculated: field === "el" ? r.calculated_used_el : field === "pl" ? r.calculated_used_pl : r.calculated_lop,
    current: field === "el" ? r.used_el : field === "pl" ? r.used_pl : r.lop_days,
    field,
    new_value: field === "el" ? r.used_el : field === "pl" ? r.used_pl : r.lop_days,
    reason_code: "", reason_text: "",
  });
  const saveAdjust = async () => {
    if (!adjusting) return;
    if (!adjusting.reason_code) { toast.error("Please select a reason"); return; }
    if (adjusting.new_value === "" || adjusting.new_value === null || Number.isNaN(parseFloat(adjusting.new_value))) {
      toast.error("Please enter a valid new value"); return;
    }
    try {
      await api.post(`/leave-ledger-v2/${adjusting.employee_id}/${adjusting.month}/adjust`, {
        field: adjusting.field,
        new_value: parseFloat(adjusting.new_value) || 0,
        reason_code: adjusting.reason_code,
        reason_text: adjusting.reason_text,
      });
      toast.success("Adjustment saved");
      const empId = adjusting.employee_id;
      setAdjusting(null);
      load();
      if (expanded === empId) {
        const { data } = await api.get(`/leave-ledger-v2/${empId}/${monthIso}/detail`);
        setDetail(data);
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  // ---------------- Lock / unlock (Step 4) ----------------
  const doLock = async () => {
    if (!window.confirm(`Lock ${monthLabel(monthIso)} for this unit? Attendance finalize and payroll recompute will stop touching this month's ledger until unlocked.`)) return;
    try {
      await api.post("/leave-ledger-v2/lock", { unit_id: unitId, month: monthIso });
      toast.success("Month locked");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };
  const doUnlock = async () => {
    if (!unlockReason.trim()) { toast.error("A reason is required to unlock"); return; }
    try {
      await api.post("/leave-ledger-v2/unlock", { unit_id: unitId, month: monthIso, reason: unlockReason });
      toast.success("Month unlocked");
      setUnlocking(false);
      setUnlockReason("");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  // ---------------- Recalculate month ----------------
  const recalculate = async () => {
    if (!window.confirm(`Recalculate the whole ledger for ${monthLabel(monthIso)} from approvals + attendance? Adjustments you've made are preserved on top.`)) return;
    try {
      const { data } = await api.post("/leave-ledger-v2/recalculate", { unit_id: unitId, month: monthIso });
      toast.success(`Recalculated ${data.updated} employees`);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  // ---------------- Export CSV ----------------
  const exportCsv = async () => {
    try {
      const { data } = await api.get("/leave-ledger-v2/export", { params: { unit_id: unitId, month: monthIso }, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([data]));
      const a = document.createElement("a");
      a.href = url; a.download = `leave_ledger_${monthIso}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
    } catch (e) {
      toast.error("Export failed");
    }
  };

  // ---------------- Audit log ----------------
  const openAudit = async () => {
    setAuditOpen(true);
    try {
      const { data } = await api.get("/leave-ledger-v2/audit", { params: { unit_id: unitId, month: monthIso } });
      setAuditRows(data || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  // ---------------- Bulk CSV adjust ----------------
  const bulkPreviewSubmit = async () => {
    if (!bulkFile) { toast.error("Choose a CSV file first"); return; }
    const fd = new FormData();
    fd.append("file", bulkFile);
    try {
      const { data } = await api.post("/leave-ledger-v2/bulk-adjust-csv/preview", fd, {
        params: { unit_id: unitId, month: monthIso },
        headers: { "Content-Type": "multipart/form-data" },
      });
      setBulkPreview(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };
  const bulkApplySubmit = async () => {
    if (!bulkPreview?.rows?.length) return;
    try {
      const { data } = await api.post("/leave-ledger-v2/bulk-adjust-csv/apply", {
        unit_id: unitId, month: monthIso, rows: bulkPreview.rows,
      });
      toast.success(`Applied ${data.applied.length} adjustments${data.failed.length ? `, ${data.failed.length} failed` : ""}`);
      setBulkOpen(false); setBulkFile(null); setBulkPreview(null);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  // ---------------- Step 6: Before/after report ----------------
  const openBeforeAfter = async () => {
    setBeforeAfterOpen(true);
    setBeforeAfterLoading(true);
    try {
      const { data } = await api.get("/leave-ledger-v2/before-after", { params: { unit_id: unitId, month: monthIso } });
      setBeforeAfter(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setBeforeAfterLoading(false);
    }
  };
  const exportBeforeAfter = async () => {
    try {
      const { data } = await api.get("/leave-ledger-v2/before-after/export", { params: { unit_id: unitId, month: monthIso }, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([data]));
      const a = document.createElement("a");
      a.href = url; a.download = `leave_ledger_before_after_${monthIso}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
    } catch (e) {
      toast.error("Export failed");
    }
  };

  // ---------------- Re-accrue (unchanged) ----------------
  const reAccrue = async (row) => {
    if (!window.confirm(`Re-accrue EL/PL for ${row.employee_name} for ${monthLabel(row.month)}?`)) return;
    try {
      const { data } = await api.post("/leave-config/re-accrue-employee", {
        employee_id: row.employee_id, month: row.month, enable_eligibility: true, bypass_probation: false,
      });
      toast.success(`Re-accrued — new +EL ${Number(data.new_accrued_el || 0).toFixed(2)}, +PL ${Number(data.new_accrued_pl || 0).toFixed(2)}`);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const filtered = useMemo(() => rows, [rows]);

  return (
    <div data-testid="leave-ledger-page" className="space-y-5">
      <div className="flex items-center gap-3 flex-wrap">
        <Wallet className="h-7 w-7 text-slate-500" />
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-widest text-slate-400">Leave Ledger v2</div>
          <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: "Chivo" }}>
            EL & PL Leave Ledger
          </h1>
        </div>
        <Button variant="outline" size="sm" onClick={openAudit} data-testid="ledger-audit-btn" disabled={!unitId}>
          <History className="h-3.5 w-3.5 mr-1.5" /> Audit log
        </Button>
        <Button variant="outline" size="sm" onClick={exportCsv} data-testid="ledger-export-btn" disabled={!unitId}>
          <Download className="h-3.5 w-3.5 mr-1.5" /> Export CSV
        </Button>
        <Button variant="outline" size="sm" onClick={() => setBulkOpen(true)} data-testid="ledger-bulk-btn" disabled={!unitId}>
          <Upload className="h-3.5 w-3.5 mr-1.5" /> Bulk adjust
        </Button>
        <Button variant="outline" size="sm" onClick={openBeforeAfter} data-testid="ledger-before-after-btn" disabled={!unitId}>
          <GitCompare className="h-3.5 w-3.5 mr-1.5" /> Before/After report
        </Button>
      </div>

      {/* Filters */}
      <div className="bg-white border border-slate-200 rounded-md p-4 flex items-center gap-3 flex-wrap">
        <div>
          <Label className="text-[10px] uppercase tracking-wider text-slate-500">Unit</Label>
          <Select value={unitId} onValueChange={setUnitId}>
            <SelectTrigger className="w-48" data-testid="ledger-unit-filter"><SelectValue placeholder="Select unit" /></SelectTrigger>
            <SelectContent>
              {units.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-wider text-slate-500">Month</Label>
          <Input type="month" value={filterMonth} onChange={(e) => setFilterMonth(e.target.value)}
            className="w-44" data-testid="ledger-month-filter" />
        </div>
        <div className="flex-1 min-w-[180px]">
          <Label className="text-[10px] uppercase tracking-wider text-slate-500">Search</Label>
          <Input placeholder="Code or name" value={search} onChange={(e) => setSearch(e.target.value)} data-testid="ledger-search" />
        </div>
        <div className="flex items-center gap-2">
          <Switch checked={adjustedOnly} onCheckedChange={setAdjustedOnly} data-testid="ledger-filter-adjusted" />
          <Label className="text-xs text-slate-600">Adjusted only</Label>
        </div>
        <div className="flex items-center gap-2">
          <Switch checked={negativeOnly} onCheckedChange={setNegativeOnly} data-testid="ledger-filter-negative" />
          <Label className="text-xs text-slate-600">Negative balance</Label>
        </div>
        <div className="ml-auto flex items-center gap-2">
          {lockInfo.locked ? (
            <Badge variant="danger" className="gap-1"><Lock className="h-3 w-3" /> Locked</Badge>
          ) : (
            <Badge variant="success" className="gap-1"><Unlock className="h-3 w-3" /> Open</Badge>
          )}
          {!lockInfo.locked && (
            <Button size="sm" variant="outline" onClick={doLock} disabled={!unitId} data-testid="ledger-lock-btn">
              <Lock className="h-3.5 w-3.5 mr-1" /> Lock month
            </Button>
          )}
          {lockInfo.locked && isSuperAdmin && (
            <Button size="sm" variant="outline" onClick={() => setUnlocking(true)} disabled={!unitId} data-testid="ledger-unlock-btn">
              <Unlock className="h-3.5 w-3.5 mr-1" /> Unlock
            </Button>
          )}
          <Button size="sm" variant="outline" onClick={recalculate} disabled={!unitId || lockInfo.locked} data-testid="ledger-recalculate-btn">
            <RefreshCw className="h-3.5 w-3.5 mr-1" /> Recalculate month
          </Button>
        </div>
      </div>

      {/* Summary cards */}
      {summary && (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-7 gap-3" data-testid="ledger-summary-cards">
          {[
            { label: "Employees", value: summary.employee_count, color: "text-slate-900" },
            { label: "EL Used", value: fmt(summary.total_used_el), color: "text-red-700" },
            { label: "PL Used", value: fmt(summary.total_used_pl), color: "text-red-700" },
            { label: "Total Leaves", value: fmt(summary.total_leaves), color: "text-slate-900" },
            { label: "Total LOP", value: fmt(summary.total_lop), color: "text-red-700" },
            { label: "Adjusted rows", value: summary.adjusted_count, color: "text-amber-700" },
            { label: "Negative balance", value: summary.negative_balance_count, color: "text-rose-700" },
          ].map((c) => (
            <div key={c.label} className="bg-white border border-slate-200 rounded-md p-3">
              <div className="text-[10px] uppercase tracking-wider text-slate-400">{c.label}</div>
              <div className={`text-lg font-bold font-mono ${c.color}`}>{c.value}</div>
            </div>
          ))}
        </div>
      )}

      {!unitId && (
        <div className="bg-white border border-slate-200 rounded-md p-10 text-center text-slate-400 text-sm">
          Select a unit to view its leave ledger.
        </div>
      )}

      {unitId && (
        <div className="bg-white border border-slate-200 rounded-md overflow-x-auto">
          <table className="w-full text-xs min-w-[1350px]">
            <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500">
              <tr>
                <th className="w-8"></th>
                <th className="text-left px-3 py-2">Employee</th>
                <th className="text-right px-3 py-2">EL Open</th>
                <th className="text-right px-3 py-2">PL Open</th>
                <th className="text-right px-3 py-2 text-green-700">+ EL</th>
                <th className="text-right px-3 py-2 text-green-700">+ PL</th>
                <th className="text-right px-3 py-2">Applied (EL/PL)</th>
                <th className="text-right px-3 py-2">Pending</th>
                <th className="text-right px-3 py-2">Shortfall</th>
                <th className="text-right px-3 py-2 text-red-700">− EL</th>
                <th className="text-right px-3 py-2 text-red-700">− PL</th>
                <th className="text-right px-3 py-2 font-semibold">Total Leaves</th>
                <th className="text-right px-3 py-2 text-red-700 font-semibold">LOP</th>
                <th className="text-right px-3 py-2 font-semibold">EL Close</th>
                <th className="text-right px-3 py-2 font-semibold">PL Close</th>
                <th className="px-3 py-2 w-10"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading && (<tr><td colSpan="16" className="text-center py-8 text-slate-400">Loading…</td></tr>)}
              {!loading && filtered.length === 0 && (
                <tr><td colSpan="16" className="text-center py-10 text-slate-400">No employees found for this filter.</td></tr>
              )}
              {!loading && filtered.map((r) => (
                <RowWithExpander
                  key={`${r.employee_id}-${r.month}`}
                  r={r} expanded={expanded === r.employee_id} detail={detail}
                  onToggle={() => toggleExpand(r)}
                  onEditOpening={() => openEdit(r)}
                  onAdjust={(field) => openAdjust(r, field)}
                  onReAccrue={() => reAccrue(r)}
                  locked={lockInfo.locked}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Opening/accrued override dialog (legacy) */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="sm:max-w-md" data-testid="ledger-edit-dialog">
          {editing && (
            <>
              <DialogHeader><DialogTitle>Opening balance override — {editing.name}</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div className="text-xs text-slate-500">
                  {editing.code} · Month: <span className="font-medium">{monthLabel(editing.month)}</span>
                </div>
                <div className="text-xs text-slate-600 bg-amber-50 border border-amber-200 rounded p-3">
                  Overrides the opening/accrued balance that carries forward from last month.
                  Leave usage (−EL/−PL/LOP) is calculated separately from approved leave —
                  use the row's <strong>Adjust</strong> buttons for that.
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-[11px] uppercase tracking-wider text-slate-500">Opening EL</Label>
                    <Input type="number" step="0.25" value={editing.opening_el}
                      onChange={(e) => setEditing({ ...editing, opening_el: e.target.value })} data-testid="opening-el-input" />
                  </div>
                  <div>
                    <Label className="text-[11px] uppercase tracking-wider text-slate-500">Opening PL</Label>
                    <Input type="number" step="0.25" value={editing.opening_pl}
                      onChange={(e) => setEditing({ ...editing, opening_pl: e.target.value })} data-testid="opening-pl-input" />
                  </div>
                  <div>
                    <Label className="text-[11px] uppercase tracking-wider text-green-700">Accrued EL</Label>
                    <Input type="number" step="0.25" value={editing.manual_accrued_el}
                      onChange={(e) => setEditing({ ...editing, manual_accrued_el: e.target.value })} data-testid="accrued-el-input" />
                  </div>
                  <div>
                    <Label className="text-[11px] uppercase tracking-wider text-green-700">Accrued PL</Label>
                    <Input type="number" step="0.25" value={editing.manual_accrued_pl}
                      onChange={(e) => setEditing({ ...editing, manual_accrued_pl: e.target.value })} data-testid="accrued-pl-input" />
                  </div>
                </div>
                <div>
                  <Label className="text-[11px] uppercase tracking-wider text-slate-500">Reason / note</Label>
                  <Input placeholder="e.g., Correcting carry-forward from last employer"
                    value={editing.reason} onChange={(e) => setEditing({ ...editing, reason: e.target.value })} data-testid="opening-reason-input" />
                </div>
              </div>
              <DialogFooter>
                <Button variant="ghost" onClick={() => setEditing(null)}>Cancel</Button>
                <Button onClick={saveOpening} className="brand-bg brand-bg-hover text-white" data-testid="opening-save-btn">Save</Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Adjustment dialog (Step 3) */}
      <Dialog open={!!adjusting} onOpenChange={(o) => !o && setAdjusting(null)}>
        <DialogContent className="sm:max-w-md" data-testid="ledger-adjust-dialog">
          {adjusting && (
            <>
              <DialogHeader>
                <DialogTitle>Adjust {adjusting.field.toUpperCase()} — {adjusting.name}</DialogTitle>
              </DialogHeader>
              <div className="space-y-3">
                <div className="text-xs text-slate-500">{adjusting.code} · {monthLabel(adjusting.month)}</div>
                <div className="text-xs text-slate-600 bg-slate-50 border border-slate-200 rounded p-3">
                  Calculated (from approvals): <span className="font-mono font-semibold">{fmt(adjusting.calculated)}</span>
                  {" · "}Current final (with adjustments): <span className="font-mono font-semibold">{fmt(adjusting.current)}</span>.
                  This change is saved as an audit entry, not an overwrite — the calculated value stays visible underneath.
                </div>
                <div>
                  <Label className="text-[11px] uppercase tracking-wider text-slate-500">New value</Label>
                  <Input type="number" step="0.25" value={adjusting.new_value}
                    onChange={(e) => setAdjusting({ ...adjusting, new_value: e.target.value })} data-testid="adjust-new-value-input" />
                </div>
                <div>
                  <Label className="text-[11px] uppercase tracking-wider text-slate-500">Reason (required)</Label>
                  <Select value={adjusting.reason_code} onValueChange={(v) => setAdjusting({ ...adjusting, reason_code: v })}>
                    <SelectTrigger data-testid="adjust-reason-select"><SelectValue placeholder="Select a reason" /></SelectTrigger>
                    <SelectContent>
                      {REASON_CODES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-[11px] uppercase tracking-wider text-slate-500">Notes (optional)</Label>
                  <Input placeholder="Any extra context" value={adjusting.reason_text}
                    onChange={(e) => setAdjusting({ ...adjusting, reason_text: e.target.value })} data-testid="adjust-notes-input" />
                </div>
              </div>
              <DialogFooter>
                <Button variant="ghost" onClick={() => setAdjusting(null)}>Cancel</Button>
                <Button onClick={saveAdjust} className="brand-bg brand-bg-hover text-white" data-testid="adjust-save-btn">Save adjustment</Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Unlock dialog */}
      <Dialog open={unlocking} onOpenChange={(o) => !o && setUnlocking(false)}>
        <DialogContent className="sm:max-w-sm" data-testid="ledger-unlock-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><AlertTriangle className="h-4 w-4 text-amber-600" /> Unlock {monthLabel(monthIso)}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Label className="text-[11px] uppercase tracking-wider text-slate-500">Reason (required)</Label>
            <Input value={unlockReason} onChange={(e) => setUnlockReason(e.target.value)} data-testid="unlock-reason-input" placeholder="Why is this month being reopened?" />
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setUnlocking(false)}>Cancel</Button>
            <Button onClick={doUnlock} variant="destructive" data-testid="unlock-confirm-btn">Unlock</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Audit log dialog */}
      <Dialog open={auditOpen} onOpenChange={setAuditOpen}>
        <DialogContent className="sm:max-w-2xl max-h-[80vh] overflow-y-auto" data-testid="ledger-audit-dialog">
          <DialogHeader><DialogTitle>Adjustment audit log — {monthLabel(monthIso)}</DialogTitle></DialogHeader>
          <table className="w-full text-xs">
            <thead className="text-[10px] uppercase text-slate-400">
              <tr><th className="text-left py-1">Employee</th><th className="text-left py-1">Field</th><th className="text-right py-1">Delta</th><th className="text-left py-1">Reason</th><th className="text-left py-1">By</th><th className="text-left py-1">When</th></tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {auditRows.length === 0 && <tr><td colSpan="6" className="text-center py-6 text-slate-400">No adjustments recorded for this month.</td></tr>}
              {auditRows.map((a) => (
                <tr key={a.id}>
                  <td className="py-1.5">{a.employee_name} <span className="text-slate-400">({a.employee_code})</span></td>
                  <td className="py-1.5 uppercase">{a.field}</td>
                  <td className={`py-1.5 text-right font-mono ${a.delta >= 0 ? "text-green-700" : "text-red-700"}`}>{a.delta > 0 ? "+" : ""}{fmt(a.delta)}</td>
                  <td className="py-1.5">{a.reason_code}{a.reason_text ? ` — ${a.reason_text}` : ""}</td>
                  <td className="py-1.5">{a.created_by_name}</td>
                  <td className="py-1.5">{a.created_at ? new Date(a.created_at).toLocaleString("en-IN") : "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </DialogContent>
      </Dialog>

      {/* Bulk CSV adjust dialog */}
      <Dialog open={bulkOpen} onOpenChange={(o) => { setBulkOpen(o); if (!o) { setBulkFile(null); setBulkPreview(null); } }}>
        <DialogContent className="sm:max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="ledger-bulk-dialog">
          <DialogHeader><DialogTitle>Bulk adjust via CSV — {monthLabel(monthIso)}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="text-xs text-slate-600 bg-slate-50 border border-slate-200 rounded p-3">
              Columns: <code>employee_code, field (el/pl/lop), new_value, reason_code, reason_text</code>
            </div>
            <Input type="file" accept=".csv" onChange={(e) => { setBulkFile(e.target.files?.[0] || null); setBulkPreview(null); }} data-testid="bulk-file-input" />
            <Button variant="outline" size="sm" onClick={bulkPreviewSubmit} disabled={!bulkFile} data-testid="bulk-preview-btn">Preview</Button>

            {bulkPreview && (
              <div className="space-y-2">
                {bulkPreview.errors?.length > 0 && (
                  <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded p-2">
                    {bulkPreview.errors.length} row(s) with errors — these will be skipped:
                    <ul className="list-disc ml-4 mt-1">
                      {bulkPreview.errors.map((er, i) => <li key={i}>Line {er.line}: {er.error}</li>)}
                    </ul>
                  </div>
                )}
                <table className="w-full text-xs">
                  <thead className="text-[10px] uppercase text-slate-400">
                    <tr><th className="text-left py-1">Employee</th><th className="text-left py-1">Field</th><th className="text-right py-1">Current</th><th className="text-right py-1">New</th><th className="text-right py-1">Δ</th><th className="text-left py-1">Reason</th></tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {bulkPreview.rows.map((r, i) => (
                      <tr key={i}>
                        <td className="py-1">{r.employee_name} ({r.employee_code})</td>
                        <td className="py-1 uppercase">{r.field}</td>
                        <td className="py-1 text-right font-mono">{fmt(r.current_value)}</td>
                        <td className="py-1 text-right font-mono">{fmt(r.new_value)}</td>
                        <td className={`py-1 text-right font-mono ${r.delta >= 0 ? "text-green-700" : "text-red-700"}`}>{r.delta > 0 ? "+" : ""}{fmt(r.delta)}</td>
                        <td className="py-1">{r.reason_code}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setBulkOpen(false)}>Cancel</Button>
            <Button onClick={bulkApplySubmit} disabled={!bulkPreview?.rows?.length} className="brand-bg brand-bg-hover text-white" data-testid="bulk-apply-btn">
              Apply {bulkPreview?.rows?.length || 0} adjustment(s)
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Step 6 — Before/after report dialog */}
      <Dialog open={beforeAfterOpen} onOpenChange={setBeforeAfterOpen}>
        <DialogContent className="sm:max-w-4xl max-h-[85vh] overflow-y-auto" data-testid="ledger-before-after-dialog">
          <DialogHeader><DialogTitle>Before/After report — {monthLabel(monthIso)}</DialogTitle></DialogHeader>
          <div className="text-xs text-slate-600 bg-slate-50 border border-slate-200 rounded p-3">
            Compares the OLD ledger formula (generic shortfall = days in month − present days) against the
            NEW engine (date-matched approved leave vs. attendance) using today's live data. Read-only — nothing is changed or saved.
          </div>
          {beforeAfterLoading && <div className="text-center py-8 text-slate-400 text-sm">Loading…</div>}
          {!beforeAfterLoading && beforeAfter && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="before-after-summary-cards">
                {[
                  { label: "Employees", value: beforeAfter.employee_count, testid: "employees" },
                  { label: "With attendance data", value: beforeAfter.with_attendance_count, testid: "with-attendance" },
                  { label: "Numbers changed", value: beforeAfter.changed_count, color: "text-amber-700", testid: "changed" },
                  { label: "Net LOP delta (days)", value: fmt(beforeAfter.total_lop_delta), color: beforeAfter.total_lop_delta !== 0 ? "text-red-700" : "", testid: "lop-delta" },
                ].map((c) => (
                  <div key={c.label} className="bg-white border border-slate-200 rounded p-2" data-testid={`before-after-card-${c.testid}`}>
                    <div className="text-[10px] uppercase tracking-wider text-slate-400">{c.label}</div>
                    <div className={`text-base font-bold font-mono ${c.color || "text-slate-900"}`}>{c.value}</div>
                  </div>
                ))}
              </div>
              {beforeAfter.with_attendance_count === 0 && (
                <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2">
                  No attendance data found for this unit/month yet — upload &amp; finalize attendance first for a meaningful comparison.
                </div>
              )}
              <div className="flex justify-end">
                <Button size="sm" variant="outline" onClick={exportBeforeAfter} data-testid="before-after-export-btn">
                  <Download className="h-3.5 w-3.5 mr-1.5" /> Export CSV
                </Button>
              </div>
              <table className="w-full text-xs">
                <thead className="text-[10px] uppercase text-slate-400">
                  <tr>
                    <th className="text-left py-1">Employee</th>
                    <th className="text-right py-1">Old −EL/−PL</th>
                    <th className="text-right py-1">New −EL/−PL</th>
                    <th className="text-right py-1">Old LOP</th>
                    <th className="text-right py-1">New LOP</th>
                    <th className="text-right py-1">Δ LOP</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {beforeAfter.rows.map((r) => (
                    <tr key={r.employee_id} className={r.changed ? "bg-amber-50" : ""} data-testid={`before-after-row-${r.employee_id}`}>
                      <td className="py-1">
                        {r.employee_name} <span className="text-slate-400">({r.employee_code})</span>
                        {!r.has_attendance && <span className="ml-1 text-[9px] uppercase text-slate-400">no attendance</span>}
                      </td>
                      <td className="py-1 text-right font-mono">{fmt(r.old_used_el)}/{fmt(r.old_used_pl)}</td>
                      <td className="py-1 text-right font-mono">{fmt(r.new_used_el)}/{fmt(r.new_used_pl)}</td>
                      <td className="py-1 text-right font-mono">{fmt(r.old_lop_days)}</td>
                      <td className="py-1 text-right font-mono">{fmt(r.new_lop_days)}</td>
                      <td className={`py-1 text-right font-mono font-semibold ${r.lop_delta !== 0 ? "text-red-700" : ""}`}>
                        {r.lop_delta > 0 ? "+" : ""}{fmt(r.lop_delta)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function DateChips({ dates }) {
  if (!dates || dates.length === 0) return <span className="text-slate-400">—</span>;
  return (
    <div className="flex flex-wrap gap-1">
      {dates.map((d) => (
        <span key={d} className="text-[10px] font-mono bg-slate-100 text-slate-700 px-1.5 py-0.5 rounded">
          {new Date(d).toLocaleDateString("en-IN", { day: "2-digit", month: "short" })}
        </span>
      ))}
    </div>
  );
}

function RowWithExpander({ r, expanded, detail, onToggle, onEditOpening, onAdjust, onReAccrue, locked }) {
  const isOverride = r.opening_override_el !== null && r.opening_override_el !== undefined;
  const negEl = r.closing_el < 0, negPl = r.closing_pl < 0;
  return (
    <>
      <tr data-testid={`ledger-row-${r.employee_id}-${r.month}`} className="hover:bg-slate-50">
        <td className="pl-3">
          <button onClick={onToggle} data-testid={`ledger-expand-${r.employee_id}`} className="text-slate-400 hover:text-slate-700">
            {expanded ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
          </button>
        </td>
        <td className="px-3 py-2">
          <div className="font-medium text-slate-900 flex items-center gap-1.5">
            {r.employee_name}
            {r.is_adjusted && <Badge variant="pending" className="text-[9px] px-1 py-0">Adjusted</Badge>}
          </div>
          <div className="text-[10px] text-slate-500 font-mono">{r.employee_code}</div>
        </td>
        <td className="px-3 py-2 text-right font-mono">
          {fmt(r.opening_el)}
          {isOverride && <span className="ml-1 text-[9px] uppercase text-amber-700 bg-amber-100 px-1 rounded">man</span>}
        </td>
        <td className="px-3 py-2 text-right font-mono">{fmt(r.opening_pl)}</td>
        <td className="px-3 py-2 text-right font-mono text-green-700">{fmt(r.accrued_el)}</td>
        <td className="px-3 py-2 text-right font-mono text-green-700">{fmt(r.accrued_pl)}</td>
        <td className="px-3 py-2 text-right font-mono text-slate-500">
          {fmt(r.applied_by_type?.EL || 0)} / {fmt(r.applied_by_type?.PL || 0)}
        </td>
        <td className="px-3 py-2 text-right font-mono text-amber-700">{r.pending_count}</td>
        <td className="px-3 py-2 text-right font-mono">{fmt(r.shortfall)}</td>
        <td className="px-3 py-2 text-right font-mono text-red-700">{fmt(r.used_el)}</td>
        <td className="px-3 py-2 text-right font-mono text-red-700">{fmt(r.used_pl)}</td>
        <td className="px-3 py-2 text-right font-mono font-semibold">{fmt(r.total_leaves)}</td>
        <td className="px-3 py-2 text-right font-mono font-semibold">
          {r.lop_days > 0 ? <span className="text-red-700">{fmt(r.lop_days)}</span> : "0.00"}
        </td>
        <td className={`px-3 py-2 text-right font-mono font-semibold ${negEl ? "text-rose-600" : ""}`}>{fmt(r.closing_el)}</td>
        <td className={`px-3 py-2 text-right font-mono font-semibold ${negPl ? "text-rose-600" : ""}`}>{fmt(r.closing_pl)}</td>
        <td className="px-3 py-2 text-right">
          <div className="flex items-center justify-end gap-1">
            <Button size="sm" variant="ghost" onClick={onReAccrue} disabled={locked} className="h-7 px-2 text-emerald-700 hover:bg-emerald-50" title="Re-accrue">
              <RefreshCw className="h-3.5 w-3.5" />
            </Button>
            <Button size="sm" variant="ghost" onClick={onEditOpening} disabled={locked} className="h-7 px-2" title="Opening balance override" data-testid={`ledger-edit-${r.employee_id}-${r.month}`}>
              <Pencil className="h-3.5 w-3.5" />
            </Button>
          </div>
        </td>
      </tr>
      {expanded && (
        <tr>
          <td colSpan="16" className="bg-slate-50 px-6 py-4 border-t border-slate-200">
            {!detail ? (
              <div className="text-xs text-slate-400 py-4">Loading detail…</div>
            ) : (
              <div className="space-y-4" data-testid={`ledger-detail-${r.employee_id}`}>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  <div className="bg-white border border-slate-200 rounded p-3 space-y-2">
                    <div className="text-[10px] uppercase tracking-wider text-slate-400">Approved this month</div>
                    {detail.breakdown.approved.length === 0 && <div className="text-xs text-slate-400">None approved.</div>}
                    {detail.breakdown.approved.map((a) => (
                      <div key={a.application_id} className="text-xs">
                        <div className="flex items-center justify-between">
                          <span className="font-medium">{a.leave_type} · {fmt(a.days_in_month)} day(s)</span>
                          <span className="text-slate-400">{a.approver || "—"}</span>
                        </div>
                        <DateChips dates={a.dates} />
                      </div>
                    ))}
                  </div>
                  <div className="bg-white border border-slate-200 rounded p-3 space-y-2">
                    <div className="text-[10px] uppercase tracking-wider text-slate-400">Pending / Rejected / Cancelled</div>
                    {[...detail.breakdown.pending, ...detail.breakdown.rejected].length === 0 && <div className="text-xs text-slate-400">None.</div>}
                    {[...detail.breakdown.pending, ...detail.breakdown.rejected].map((a) => (
                      <div key={a.application_id} className="text-xs">
                        <div className="flex items-center justify-between">
                          <span className="font-medium">{a.leave_type} · {a.status} · {fmt(a.days_in_month)} day(s)</span>
                        </div>
                        <DateChips dates={a.dates} />
                      </div>
                    ))}
                  </div>
                </div>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  <div className="bg-white border border-slate-200 rounded p-3 space-y-2">
                    <div className="text-[10px] uppercase tracking-wider text-slate-400">Unapproved absence (= Shortfall, feeds LOP)</div>
                    <DateChips dates={detail.absence.unapproved_absence_dates} />
                  </div>
                  <div className="bg-white border border-slate-200 rounded p-3 space-y-2">
                    <div className="text-[10px] uppercase tracking-wider text-slate-400 flex items-center justify-between">
                      Adjustments on this month
                      <div className="flex gap-1">
                        {["el", "pl", "lop"].map((f) => (
                          <Button key={f} size="sm" variant="outline" className="h-6 text-[10px] px-2" disabled={locked}
                            onClick={() => onAdjust(f)} data-testid={`ledger-adjust-${f}-${r.employee_id}`}>
                            Adjust {f.toUpperCase()}
                          </Button>
                        ))}
                      </div>
                    </div>
                    {(!detail.adjustments || detail.adjustments.length === 0) && <div className="text-xs text-slate-400">No adjustments.</div>}
                    {(detail.adjustments || []).map((a) => (
                      <div key={a.id} className="text-xs flex items-center justify-between">
                        <span>{a.field.toUpperCase()} {a.delta > 0 ? "+" : ""}{fmt(a.delta)} — {a.reason_code}</span>
                        <span className="text-slate-400">{a.created_by_name}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </td>
        </tr>
      )}
    </>
  );
}
